/* ============================================================================
   haiku-fitsticky — the product details panel is sticky only when it fits

   THE PROBLEM. product-editorial.css makes `.pe__panel` `position: sticky`.
   That is right while the panel is shorter than the window: it holds beside
   the photographs as they scroll past. It is wrong the moment the panel is
   taller, because a sticky element pins its TOP and the page scrolls on
   underneath — so everything below the fold, the specs list in particular,
   can never be brought into view at all. Measured at 1440x900: a 915px panel
   inside an 864px sticky window, with the last 51px permanently unreachable.

   WHY THIS IS JAVASCRIPT. CSS cannot ask the question. An element cannot be
   styled from its own content height, and a container query reports on the
   container rather than on itself, so there is no selector that means "only
   while you fit". A measurement is the only honest answer.

   WHAT IT DOES. Two things, and the second depends on the first.

   1. Measures BOTH blur bands (`.hb.hb--top`, `.hb.hb--bottom`) and publishes
      their heights on :root as `--hk-band-top-px` and `--hk-band-bottom-px`.
      haiku-item.css adds the top one to the panel's sticky offset, so the
      panel pins BELOW the band instead of underneath it — pinned at 36px
      inside a 144px band, the title was permanently dissolved. The bottom one
      is used by the fit test below.

      Measured rather than read from `--hb-size-top`, because that token only
      exists when snippets/haiku-blur.liquid renders it and the band itself is
      drawn inside an `@supports (backdrop-filter)` guard. A token fallback
      would push the panel down for a band that is not on the page. A
      measurement of 0 is the truth in exactly those cases.

   2. Compares the panel's natural height against the room the window leaves
      once that sticky offset and a little air are taken out, and writes
      `data-pe-fits="1"` or `"0"`. haiku-item.css turns sticky OFF on "0" and
      does nothing at all on "1".

      Each band leaves the room exactly once. The top one is already inside
      that offset, so subtracting it again would count it twice; the bottom
      one is in no offset at all and is taken out on its own.

   FAIL-SAFE, in both directions. If this file never parses, no attribute is
   ever written, no rule matches, and the panel is sticky exactly as the theme
   ships it — the state the storefront has had all along. And the CSS only
   ever REMOVES sticky, so a wrong measurement can cost a nicety, never a
   reachable page.

   ⚠️ Read `top` off the panel, not `--pe-sticky-top`. The custom property is
   authored as `clamp(24px, 4vh, 56px)` and getPropertyValue hands back that
   token verbatim, unresolved. The computed `top` of a sticky element is the
   used value — an actual px length. That is also why the attribute is cleared
   before measuring: with `position: static` in force, `top` computes to
   `auto` and the measurement would silently read 0.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuFitStickyConfig || {};
  if (cfg.enabled === false) return;

  var SEL = cfg.selector || '[data-pe-panel]';
  /* Air below the panel so "it fits" does not mean "it ends exactly on the
     bottom edge of the screen". */
  var AIR = typeof cfg.air === 'number' ? cfg.air : 24;

  /* The band's real drawn height, or 0 when there is no band on the page —
     it is absent, switched off, or the browser has no backdrop-filter and the
     whole of haiku-blur.css is inert. */
  function bandHeight(sel) {
    var b = document.querySelector(sel);
    if (!b) return 0;
    var cs = window.getComputedStyle(b);
    if (cs.display === 'none' || cs.visibility === 'hidden') return 0;
    return Math.round(b.getBoundingClientRect().height);
  }

  function measure() {
    var top = bandHeight('.hb.hb--top');
    var bottom = bandHeight('.hb.hb--bottom');

    document.documentElement.style.setProperty('--hk-band-top-px', top + 'px');
    document.documentElement.style.setProperty('--hk-band-bottom-px', bottom + 'px');

    var panels = document.querySelectorAll(SEL);
    for (var i = 0; i < panels.length; i++) {
      var panel = panels[i];

      /* Back to sticky first, or `top` reads `auto` — see the note above. */
      panel.removeAttribute('data-pe-fits');

      var cs = window.getComputedStyle(panel);
      if (cs.position !== 'sticky') continue;   // static below 1000px by design

      var stickyOffset = parseFloat(cs.top);
      if (!isFinite(stickyOffset)) stickyOffset = 0;

      var height = panel.getBoundingClientRect().height;

      /* Both bands come out of the room, and each exactly once. The TOP one is
         already inside the sticky offset read above, which is why it is not
         subtracted again here. The BOTTOM one is not in any offset -- nothing
         pushes the panel up off the floor -- so it is taken out on its own, or
         the last spec row would sit in the dissolve and count as "fitting". */
      var room = window.innerHeight - stickyOffset - bottom - AIR;

      panel.setAttribute('data-pe-fits', height <= room ? '1' : '0');
    }
  }

  var pending = null;
  function schedule() {
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(function () {
      pending = null;
      measure();
    }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', measure);
  } else {
    measure();
  }

  /* The panel's height is type, so it is not final until the faces are. */
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(measure);
  }

  /* Images above the panel can still reflow it after DOMContentLoaded, and the
     reveal animation settles a little after that. Same belt-and-braces
     re-runs haiku-row.js uses for the same reason. */
  window.addEventListener('load', measure);
  window.setTimeout(measure, 900);
  window.setTimeout(measure, 2400);

  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
})();
